import {
  Navigate,
  RouterProvider,
  createBrowserRouter,
  useLocation,
  useParams,
} from "react-router"

import { ErrorPage } from "@/app/error"
import NotFound from "@/app/not-found"
import { useProjectSmart } from "@/hooks/use-project-smart"

// The project home depends on the viewer. Members land on the endpoints page
// (raw metrics look empty until an app has been running for a while, so
// endpoints is a better first landing). Public / non-member visitors are sent
// to the metrics page, which is the only project route that supports
// unauthenticated public viewing — endpoints requires the Base entitlement and
// an authenticated session.
function RedirectToProjectHome() {
  const { username, projectName, isLoading, isPublic } = useProjectSmart()
  // Wait for the session + project lookup to resolve so a logged-in member is
  // never briefly misrouted to the public metrics view.
  if (isLoading) return null
  const home = isPublic ? "metrics" : "endpoints"
  return <Navigate to={`/${username}/${projectName}/${home}`} replace />
}

// Net-diagnostics used to live under /endpoints/net-diagnostics. Preserves
// bookmarks, in-app links rendered before this release, and old alert deep
// links pointing at the previous path.
function RedirectToNetDiagnostics() {
  const { username, projectName } = useParams()
  const { search, hash } = useLocation()
  return (
    <Navigate
      to={`/${username}/${projectName}/net-diagnostics${search}${hash}`}
      replace
    />
  )
}

function RedirectToNetDiagnosticsReport() {
  const { username, projectName, reportId } = useParams()
  const { search, hash } = useLocation()
  return (
    <Navigate
      to={`/${username}/${projectName}/net-diagnostics/report/${reportId}${search}${hash}`}
      replace
    />
  )
}

const router = createBrowserRouter([
  {
    path: "/",
    lazy: () => import("./signup/page"),
    errorElement: <ErrorPage />,
  },
  {
    path: "/:username",
    lazy: () => import("./[username]/layout"),
    errorElement: <ErrorPage />,
    children: [
      {
        path: "",
        lazy: () => import("./[username]/page"),
      },
      {
        path: ":projectName",
        lazy: () => import("./[username]/[project-name]/layout"),
        children: [
          {
            path: "",
            element: <RedirectToProjectHome />,
          },
          {
            path: "metrics",
            lazy: () => import("./[username]/[project-name]/metrics/page"),
          },
          {
            path: "onboarding",
            lazy: () => import("./[username]/[project-name]/onboarding/page"),
          },
          {
            path: "settings",
            lazy: () => import("./[username]/[project-name]/settings/layout"),
            children: [
              {
                path: "",
                lazy: () => import("./[username]/[project-name]/settings/page"),
              },
              {
                path: "collaborators",
                lazy: () =>
                  import(
                    "./[username]/[project-name]/settings/collaborators/page"
                  ),
              },
              {
                path: "general",
                lazy: () =>
                  import("./[username]/[project-name]/settings/general/page"),
              },
              {
                path: "billing",
                lazy: () =>
                  import("./[username]/[project-name]/settings/billing/page"),
              },
              {
                path: "api-keys",
                element: <Navigate to="../../api-keys" replace />,
              },
              {
                path: "activity",
                lazy: () =>
                  import("./[username]/[project-name]/settings/activity/page"),
              },
            ],
          },
          {
            path: "api-keys",
            lazy: () => import("./[username]/[project-name]/api-keys/layout"),
            children: [
              {
                path: "",
                lazy: () => import("./[username]/[project-name]/api-keys/page"),
              },
            ],
          },
          {
            path: "relays",
            lazy: () => import("./[username]/[project-name]/relays/layout"),
            children: [
              {
                path: "",
                lazy: () => import("./[username]/[project-name]/relays/page"),
              },
              {
                path: "setup",
                lazy: () =>
                  import("./[username]/[project-name]/relays/setup/page"),
              },
              {
                path: "settings",
                lazy: () =>
                  import("./[username]/[project-name]/relays/settings/page"),
              },
              {
                path: "deploy",
                lazy: () =>
                  import("./[username]/[project-name]/relays/deploy/page"),
              },
            ],
          },
          {
            path: "dns",
            lazy: () => import("./[username]/[project-name]/dns/layout"),
            children: [
              {
                path: "",
                lazy: () => import("./[username]/[project-name]/dns/page"),
              },
              {
                path: "setup",
                lazy: () =>
                  import("./[username]/[project-name]/dns/setup/page"),
              },
              {
                path: "settings",
                lazy: () =>
                  import("./[username]/[project-name]/dns/settings/page"),
              },
            ],
          },
          {
            path: "endpoints",
            lazy: () => import("./[username]/[project-name]/endpoints/layout"),
            children: [
              {
                path: "",
                lazy: () =>
                  import("./[username]/[project-name]/endpoints/page"),
              },
              {
                path: "list",
                lazy: () =>
                  import("./[username]/[project-name]/endpoints/list/page"),
              },
              {
                path: "net-diagnostics",
                element: <RedirectToNetDiagnostics />,
              },
              {
                path: "net-diagnostics/report/:reportId",
                element: <RedirectToNetDiagnosticsReport />,
              },
              {
                path: ":nodeId",
                lazy: () =>
                  import(
                    "./[username]/[project-name]/endpoints/[node-id]/page"
                  ),
              },
            ],
          },
          {
            path: "net-diagnostics",
            lazy: () =>
              import("./[username]/[project-name]/net-diagnostics/page"),
          },
          {
            path: "net-diagnostics/report/:reportId",
            lazy: () =>
              import("./[username]/[project-name]/net-diagnostics/report/page"),
          },
          {
            path: "workflows",
            lazy: () => import("./[username]/[project-name]/workflows/layout"),
            children: [
              {
                path: "",
                lazy: () =>
                  import("./[username]/[project-name]/workflows/page"),
              },
              {
                path: ":workflowId",
                lazy: () =>
                  import(
                    "./[username]/[project-name]/workflows/[workflow-id]/page"
                  ),
              },
            ],
          },
          {
            path: "change-plan",
            lazy: () =>
              import("./[username]/[project-name]/change-plan/layout"),
            children: [
              {
                path: "",
                lazy: () =>
                  import("./[username]/[project-name]/change-plan/page"),
              },
            ],
          },
        ],
      },
      {
        path: "profile",
        lazy: () => import("./[username]/settings/layout"),
        children: [
          {
            path: "",
            lazy: () => import("./[username]/settings/page"),
          },
          {
            path: "connected-accounts",
            lazy: () => import("./[username]/settings/connected-accounts/page"),
          },
          {
            path: "security",
            lazy: () => import("./[username]/settings/security/page"),
          },
        ],
      },

      {
        path: "projects/new",
        lazy: () => import("./[username]/projects/new/page"),
      },
    ],
  },
  {
    path: "/staff",
    lazy: () => import("./staff/layout"),
    errorElement: <ErrorPage />,
    children: [
      {
        path: "",
        lazy: () => import("./staff/page"),
      },
      {
        path: "users",
        lazy: () => import("./staff/users/page"),
      },
      {
        path: "users/:userId",
        lazy: () => import("./staff/users/[userId]/page"),
      },
      {
        path: "projects",
        lazy: () => import("./staff/projects/page"),
      },
      {
        path: "projects/:projectId",
        lazy: () => import("./staff/projects/[projectId]/layout"),
        children: [
          {
            index: true,
            element: <Navigate to="overview" replace />,
          },
          {
            path: "overview",
            lazy: () => import("./staff/projects/[projectId]/tabs/overview"),
          },
          {
            path: "entitlements",
            lazy: () =>
              import("./staff/projects/[projectId]/entitlements/page"),
          },
          {
            path: "usage",
            lazy: () => import("./staff/projects/[projectId]/usage/page"),
          },
          {
            path: "metrics",
            lazy: () => import("./staff/projects/[projectId]/metrics/page"),
          },
          {
            path: "deploys",
            lazy: () => import("./staff/projects/[projectId]/deploys/page"),
          },
          {
            path: "collaborators",
            lazy: () =>
              import("./staff/projects/[projectId]/collaborators/page"),
          },
        ],
      },
      {
        path: "upcoming-invoices",
        lazy: () => import("./staff/upcoming-invoices/page"),
      },
      {
        path: "support-tickets",
        lazy: () => import("./staff/support-tickets/page"),
      },
      {
        path: "support-tickets/new",
        lazy: () => import("./staff/support-tickets/new/page"),
      },
      {
        path: "support-tickets/:ticketNumber",
        lazy: () => import("./staff/support-tickets/[ticketNumber]/page"),
      },
      {
        path: "infrastructure",
        lazy: () => import("./staff/infrastructure/page"),
      },
      {
        path: "builds",
        lazy: () => import("./staff/builds/page"),
      },
    ],
  },
  {
    path: "/login",
    errorElement: <ErrorPage />,
    children: [
      { path: "", lazy: () => import("./login/page") },
      { path: "forgot", lazy: () => import("./login/forgot/page") },
      { path: "reset/:token", lazy: () => import("./login/reset/page") },
    ],
  },
  {
    path: "invite/:code",
    lazy: () => import("./invite/page"),
    errorElement: <ErrorPage />,
  },
  {
    path: "/verify/:token",
    lazy: () => import("./verify/page"),
    errorElement: <ErrorPage />,
  },
  {
    path: "/signup",
    lazy: () => import("./signup/page"),
    errorElement: <ErrorPage />,
  },

  // static site bits
  {
    path: "/pricing",
    lazy: () => import("./pricing/page"),
    errorElement: <ErrorPage />,
  },
  {
    path: "legal",
    lazy: () => import("./legal/layout"),
    errorElement: <ErrorPage />,
    children: [
      {
        path: "",
        lazy: () => import("./legal/page"),
      },
      {
        path: "privacy",
        lazy: () => import("./legal/privacy/page"),
      },
      {
        path: "terms",
        lazy: () => import("./legal/terms/page"),
      },
    ],
  },
  {
    path: "/style",
    lazy: () => import("./style/page"),
    errorElement: <ErrorPage />,
  },
  {
    path: "*",
    element: <NotFound />,
  },
])

export function Router() {
  return <RouterProvider router={router} />
}
